import { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, FileText, FileUp, BookOpen, Paperclip, X, ClipboardList } from "lucide-react";
import * as XLSX from "xlsx";
import { AppShell } from "../layouts/AppShell";
import { Button } from "../primitives/Button";
import {
  listEmailTemplates,
  listEmailRegister,
  listSurveyPresentationResponses,
  exportAmbassadeurLetterPdf,
  sendBulkEmail,
  sendBulkEmailWithFiles,
  sendBulkEmailListWithAttachments,
  sendBulkEmailFromFile,
  type EmailTemplateItem,
  type EmailSendLogItem,
  type SendEmailResult,
  type SurveyPresentationResponseItem,
} from "../../api/emailSend";
import { listFarms, listFarmContacts, type FarmContact } from "../../api/admin";
import { cn } from "../utils/cn";
import { hasPermissionInCurrentOrg } from "../../state/orgContext";

const SENDER_EMAIL_OPTIONS = [
  { value: "", label: "Par défaut (info@xlkey.ca)" },
  { value: "info@xlkey.ca", label: "info@xlkey.ca" },
  { value: "valerie@firstphosphate.com", label: "valerie@firstphosphate.com" },
  { value: "bennett@firstphosphate.com", label: "bennett@firstphosphate.com" },
];

const DEFAULT_LIST_TEMPLATE = "phos_drs_feb27_2026";
const DEFAULT_LIST_SUBJECT = "DRS FEB 27 2026";
const DEFAULT_LIST_FROM_NAME = "First Phosphate Corp.";
const DEFAULT_LIST_FROM_EMAIL = "bennett@firstphosphate.com";

const AMBASSADEUR_TEMPLATE_CODE = "ambassadeur_lettre_engagement_01";
const AMBASSADEUR_FROM_NAME = "XLKey";
const AMBASSADEUR_FROM_EMAIL = "victormorin@xlkey.ca";

const DRONE_PHASE2_TEMPLATE_CODE = "drone_academie_phase2";
const DRONE_PHASE2_FROM_NAME = "XLKey";
const DRONE_PHASE2_FROM_EMAIL = "yannickgoulet@xlkey.ca";

type DroneScheduleMode = "omit" | "tbd" | "scheduled";

const DRONE_SCHEDULE_MODE_OPTIONS: { value: DroneScheduleMode; label: string }[] = [
  { value: "omit", label: "Ne pas mentionner" },
  { value: "tbd", label: "À déterminer" },
  { value: "scheduled", label: "Date et heure précises" },
];

const DRONE_VENUE_MODE_OPTIONS: { value: DroneScheduleMode; label: string }[] = [
  { value: "omit", label: "Ne pas mentionner" },
  { value: "tbd", label: "À déterminer" },
  { value: "scheduled", label: "Adresse précise" },
];

const SUIVI_TEMPLATE_CODE = "suivi_presentation_xlkey";
const SUIVI_FROM_NAME = "Victor Morin - XLKey";
const SUIVI_FROM_EMAIL = "victormorin@xlkey.ca";

const PESTICIDE_DRONE_TEMPLATE_CODE = "pesticide_drone";
const PESTICIDE_DRONE_FROM_NAME = "XLKey";
const PESTICIDE_DRONE_FROM_EMAIL = "info@xlkey.ca";
const PESTICIDE_DRONE_CC_DEFAULT =
  "yannickgoulet@xlkey.ca, simoncarrier@xlkey.ca, info@xlkey.ca";
const PESTICIDE_DRONE_BCC_DEFAULT = "victormorin@xlkey.ca";
/** Aligné sur `PESTICIDE_DRONE_DEFAULT_SUBJECT` côté backend. */
const PESTICIDE_DRONE_DEFAULT_SUBJECT =
  "INFO - Application de pesticides par drone";

const ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE = "annonce_pulverisation_drone_ca";
const ANNONCE_PULVERISATION_DRONE_CA_FROM_NAME = "XLKey";
const ANNONCE_PULVERISATION_DRONE_CA_FROM_EMAIL = "info@xlkey.ca";
const ANNONCE_PULVERISATION_DRONE_CA_DEFAULT_SUBJECT =
  "Pulvérisation par drone — c'est officiel au Canada";
const ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT = "https://xlkey.ca/contact";
const ANNONCE_PULVERISATION_DRONE_CA_CC_DEFAULT = "info@xlkey.ca";

const INFO_REFERENCE_ASSURANCE_DRONE_TEMPLATE_CODE = "info_reference_assurance_drone";
const INFO_REFERENCE_ASSURANCE_DRONE_DEFAULT_SUBJECT =
  "Assurance drone : coordonnées de notre courtier partenaire";

const XLHUB_DRAINAGE_TEMPLATE_CODE = "xlhub_drainage_formats";
const XLHUB_DRAINAGE_FROM_NAME = "XLKey";
const XLHUB_DRAINAGE_FROM_EMAIL = "info@xlkey.ca";
const XLHUB_DRAINAGE_DEFAULT_SUBJECT = "XLHUB - Formats de données de drainage";

const ACCES_JOHNDEERE_TEMPLATE_CODE = "acces_johndeere";
const ACCES_JOHNDEERE_FROM_NAME = "XLKey";
const ACCES_JOHNDEERE_FROM_EMAIL = "info@xlkey.ca";
const ACCES_JOHNDEERE_DEFAULT_SUBJECT = "Accès JohnDeere";

const XLKEY_CONTACT_OPTIONS = [
  { id: "simon" as const, label: "Simon Carrier, agr." },
  { id: "victor" as const, label: "Victor Morin" },
  { id: "yannick" as const, label: "Yannick Goulet" },
];

const TEMPLATE_CONTACT_DEFAULTS: Record<
  string,
  { simon: boolean; victor: boolean; yannick: boolean }
> = {
  acces_johndeere: { simon: true, victor: false, yannick: false },
  drone_immatriculation_tc: { simon: true, victor: false, yannick: true },
  pesticide_drone: { simon: true, victor: false, yannick: true },
  annonce_pulverisation_drone_ca: { simon: true, victor: true, yannick: false },
  xlhub_drainage_formats: { simon: true, victor: false, yannick: false },
  agras_flight_parameters: { simon: false, victor: false, yannick: true },
  drone_academie_phase2: { simon: false, victor: false, yannick: true },
  suivi_presentation_xlkey: { simon: false, victor: true, yannick: false },
  offre_2026_ambassadeur: { simon: false, victor: true, yannick: false },
};

const DRONE_IMMATRICULATION_TC_TEMPLATE_CODE = "drone_immatriculation_tc";
const DRONE_IMMATRICULATION_TC_FROM_NAME = "XLKey";
const DRONE_IMMATRICULATION_TC_FROM_EMAIL = "info@xlkey.ca";
const DRONE_IMMATRICULATION_TC_DEFAULT_SUBJECT =
  "INFO - Immatriculation de votre drone chez Transport Canada";

const AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE = "agras_flight_parameters";
const AGRAS_FLIGHT_PARAMETERS_FROM_NAME = "XLKey";
const AGRAS_FLIGHT_PARAMETERS_FROM_EMAIL = "info@xlkey.ca";

const AVIS_GOOGLE_XLKEY_TEMPLATE_CODE = "avis_google_xlkey";
const AVIS_GOOGLE_XLKEY_FROM_NAME = "XLKey";
const AVIS_GOOGLE_XLKEY_FROM_EMAIL = "info@xlkey.ca";
const AVIS_GOOGLE_XLKEY_DEFAULT_SUBJECT = "Merci pour votre confiance — votre avis sur Google";
const AVIS_GOOGLE_XLKEY_DEFAULT_REVIEW_URL = "https://g.page/r/CaQHgI3h1NBcEBM/review";

const AGRAS_DRONE_OPTIONS = [
  { id: "t100", label: "DJI AGRAS T100" },
  { id: "t50", label: "DJI AGRAS T50" },
  { id: "t25p", label: "DJI AGRAS T25P" },
] as const;

function buildAgrasDefaultSubject(modelIds: string[]): string {
  const labels = AGRAS_DRONE_OPTIONS.filter((opt) => modelIds.includes(opt.id)).map((opt) =>
    opt.label.replace("DJI AGRAS ", "")
  );
  const selected = labels.length
    ? labels.length === 1
      ? labels[0]
      : labels.length === 2
        ? labels.join(" et ")
        : `${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}`
    : "T100, T50 et T25P";
  return `Fiche technique - Paramètres de vol AGRAS ${selected}`;
}

/** Formate une valeur input datetime-local pour le corps du courriel (fr-CA). */
function formatFrenchDateTimeFromLocal(isoLocal: string): string {
  if (!isoLocal || isoLocal.length < 16) return "";
  const d = new Date(isoLocal);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "long", timeStyle: "short" }).format(d);
}

/** Extrait YYYY-MM-DD et HH:MM pour l’invitation calendrier (.ics). */
function splitLocalDateTime(isoLocal: string): { date: string; time: string } | null {
  if (!isoLocal || isoLocal.length < 16) return null;
  const [date, rest] = isoLocal.split("T");
  const time = (rest || "00:00").slice(0, 5);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return null;
  return { date: date!, time };
}

/** CC : garantit victormorin@xlkey.ca sans doublon. */
function mergeCcWithAmbassadorDefault(ccRaw: string): string {
  const vic = AMBASSADEUR_FROM_EMAIL.toLowerCase();
  const parts = ccRaw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set(parts.map((p) => p.toLowerCase()));
  if (!seen.has(vic)) parts.unshift(AMBASSADEUR_FROM_EMAIL);
  return parts.join(", ");
}

const DRS_TEMPLATE_ITEM: EmailTemplateItem = {
  code: DEFAULT_LIST_TEMPLATE,
  name: "PHOS - DRS FEB 27 2026",
  description: "DRS statement vested RSUs (EN, bouton confirmation réception)",
};

/** Message affiché quand le backend ne connaît pas le template DRS (backend à mettre à jour). */
const TEMPLATE_INCONNU_MSG =
  "Le serveur ne reconnaît pas le template « PHOS - DRS FEB 27 2026 ». Redéployez le backend (version actuelle du dépôt) sur ce serveur pour activer ce template.";

const EMAIL_ADDRESSES_SEPARATOR_HINT =
  "Plusieurs adresses : espace, virgule (,) ou point-virgule (;)";

function parseEmailList(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
}

function formatSurveyAnswerCell(v: unknown): string {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join("; ") : "—";
  return String(v);
}

function csvEscape(value: string): string {
  if (value.includes(";") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

type SendWithFilesResult = {
  sent: number;
  total: number;
  errors: { email: string; dossier?: string; error: string }[];
  skipped_no_email?: { dossier: string; nom: string; prenom: string; raison?: string }[];
  sent_recipients?: { email: string; dossier: string; nom: string; prenom: string }[];
  skipped_duplicates?: number;
};

function exportSendResultToCsv(res: SendWithFilesResult): void {
  const header = "Statut;Courriel;Dossier;Nom;Prénom;Raison";
  const rows: string[] = [header];
  const sent = res.sent_recipients ?? [];
  sent.forEach((r) => {
    rows.push([ "Envoyé", r.email, r.dossier, r.nom, r.prenom, "OK" ].map(csvEscape).join(";"));
  });
  (res.errors ?? []).forEach((e) => {
    rows.push([ "Échec", e.email, e.dossier ?? "", "", "", e.error ].map(csvEscape).join(";"));
  });
  (res.skipped_no_email ?? []).forEach((s) => {
    const raison = s.raison ?? "Courriel manquant ou invalide";
    rows.push([ "Non envoyé", "", s.dossier, s.nom, s.prenom, raison ].map(csvEscape).join(";"));
  });
  const csv = "\uFEFF" + rows.join("\r\n"); // BOM for Excel UTF-8
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `recapitulatif-envoi-courriel-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

type EmailSendTab = "liste" | "fichier" | "registre" | "sondage";

const STATUS_LABELS: Record<string, string> = {
  sent: "Envoyé",
  delivered: "Reçu",
  opened: "Ouvert",
  replied: "Répondu",
  failed: "Échec",
};

function formatReadDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "";
  if (seconds < 60) return `${seconds} s`;
  const min = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${min} min ${s} s` : `${min} min`;
}

/** Libellé liste déroulante : nom + description si présente. */
function formatTemplateOptionLabel(t: EmailTemplateItem): string {
  const d = (t.description ?? "").trim();
  return d ? `${t.name} — ${d}` : t.name;
}

const REGISTER_EXPORT_COLUMNS = [
  "id",
  "Date d'envoi",
  "Destinataire (To)",
  "Sujet",
  "Modèle",
  "Expéditeur (courriel)",
  "Expéditeur (nom)",
  "Statut",
  "Message d'erreur",
  "Nombre d'ouvertures",
  "Durée lecture (est.)",
] as const;

function registerRowToExportRow(row: EmailSendLogItem): string[] {
  return [
    row.id,
    row.sent_at ?? "",
    row.to_email,
    row.subject,
    row.template_code ?? "",
    row.from_email ?? "",
    row.from_name ?? "",
    row.status,
    row.error_message ?? "",
    String(row.open_count ?? 0),
    row.read_duration_seconds != null ? formatReadDuration(row.read_duration_seconds) : "",
  ];
}

export function EmailSendPage({ userName = "Utilisateur" }: { userName?: string }) {
  const canBookings = hasPermissionInCurrentOrg("commercial.bookings.read");
  const commercialModules = useMemo(
    () => [
      { label: "Commercial", sectionHeader: true },
      { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
      { label: "Envoi de courriel", onClick: () => (window.location.hash = "#/email-send") },
      ...(canBookings ? [{ label: "Rendez-vous", onClick: () => (window.location.hash = "#/bookings") }] : []),
      { label: "Clients", onClick: () => (window.location.hash = "#/clients") },
      { label: "Ambassadeurs", onClick: () => (window.location.hash = "#/ambassadors") },
      { label: "Pilotes", onClick: () => (window.location.hash = "#/pilots") },
      { label: "Templates de courriel", onClick: () => (window.location.hash = "#/admin-email-templates") },
    ],
    [canBookings]
  );
  const [activeTab, setActiveTab] = useState<EmailSendTab>("liste");
  const [templates, setTemplates] = useState<EmailTemplateItem[]>([DRS_TEMPLATE_ITEM]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedCode, setSelectedCode] = useState(DEFAULT_LIST_TEMPLATE);
  const [subject, setSubject] = useState(DEFAULT_LIST_SUBJECT);
  const [fromName, setFromName] = useState<string>(DEFAULT_LIST_FROM_NAME);
  const [fromEmail, setFromEmail] = useState(DEFAULT_LIST_FROM_EMAIL);
  const [ccText, setCcText] = useState("");
  /** CCI (copie invisible) — ex. template pesticide_drone */
  const [bccText, setBccText] = useState("");
  const [greetingName, setGreetingName] = useState("");
  /** Entente de collaboration (ambassadeur) : nom d'entreprise pour le sujet automatique et le mailto de confirmation. */
  const [confirmationName, setConfirmationName] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [emailListText, setEmailListText] = useState("");
  /** Drone Académie Phase 2 : plages horaires + ferme / contact CRM */
  const [droneFormationMode, setDroneFormationMode] = useState<DroneScheduleMode>("tbd");
  const [droneRevisionMode, setDroneRevisionMode] = useState<DroneScheduleMode>("tbd");
  const [droneVenueMode, setDroneVenueMode] = useState<DroneScheduleMode>("tbd");
  const [droneFormationLocal, setDroneFormationLocal] = useState("");
  const [droneRevisionLocal, setDroneRevisionLocal] = useState("");
  const [droneVenue, setDroneVenue] = useState("");
  const [droneParticipantBringsDrone, setDroneParticipantBringsDrone] = useState(false);
  const [droneParticipantBringsSafety, setDroneParticipantBringsSafety] = useState(false);
  const [emailFarmOptions, setEmailFarmOptions] = useState<{ id: string; name: string }[]>([]);
  const [emailFarmLoading, setEmailFarmLoading] = useState(false);
  const [selectedFarmIdEmail, setSelectedFarmIdEmail] = useState("");
  const [farmContactsForEmail, setFarmContactsForEmail] = useState<FarmContact[]>([]);
  const [contactsLoadingForEmail, setContactsLoadingForEmail] = useState(false);
  const [selectedContactIdEmail, setSelectedContactIdEmail] = useState("");
  const [tcPurchaseDate, setTcPurchaseDate] = useState("");
  const [tcDroneBrand, setTcDroneBrand] = useState("");
  const [tcDroneModel, setTcDroneModel] = useState("");
  const [tcDroneSerialNumber, setTcDroneSerialNumber] = useState("");
  const [tcDroneType, setTcDroneType] = useState("");
  const [showSimon, setShowSimon] = useState(true);
  const [showVictor, setShowVictor] = useState(false);
  const [showYannick, setShowYannick] = useState(true);
  const [agrasDroneModels, setAgrasDroneModels] = useState<string[]>(
    AGRAS_DRONE_OPTIONS.map((opt) => opt.id)
  );
  const [sending, setSending] = useState(false);
  const [exportingAmbassadeurPdf, setExportingAmbassadeurPdf] = useState(false);
  const [result, setResult] = useState<SendEmailResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [annonceBulkRecipientsFile, setAnnonceBulkRecipientsFile] = useState<File | null>(null);

  // Onglet "Avec Fichier"
  const [templateWithFiles, setTemplateWithFiles] = useState("");
  const [subjectWithFiles, setSubjectWithFiles] = useState("");
  const [fromEmailWithFiles, setFromEmailWithFiles] = useState("");
  const [fromNameWithFiles, setFromNameWithFiles] = useState<string>("First Phosphate Corp.");
  const [ccWithFiles, setCcWithFiles] = useState("");
  const [cciWithFiles, setCciWithFiles] = useState("");
  const [recipientsFile, setRecipientsFile] = useState<File | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [sendingWithFiles, setSendingWithFiles] = useState(false);
  const [resultWithFiles, setResultWithFiles] = useState<SendWithFilesResult | null>(null);

  // Onglet Registre
  const [registerItems, setRegisterItems] = useState<EmailSendLogItem[]>([]);
  const [registerTotal, setRegisterTotal] = useState(0);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerFilterInput, setRegisterFilterInput] = useState("");
  const [registerFilterApplied, setRegisterFilterApplied] = useState("");
  const [registerDateFrom, setRegisterDateFrom] = useState("");
  const [registerDateTo, setRegisterDateTo] = useState("");
  const [registerExporting, setRegisterExporting] = useState(false);

  const [bookingUrl, setBookingUrl] = useState("");
  const [contactFormUrl, setContactFormUrl] = useState(ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT);
  const [googleReviewUrl, setGoogleReviewUrl] = useState(AVIS_GOOGLE_XLKEY_DEFAULT_REVIEW_URL);
  /** Suivi XLKey : lien sondage partageable (un seul destinataire relais). */
  const [surveyBroadcast, setSurveyBroadcast] = useState(false);
  const [surveyItems, setSurveyItems] = useState<SurveyPresentationResponseItem[]>([]);
  const [surveyTotal, setSurveyTotal] = useState(0);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyLoadError, setSurveyLoadError] = useState<string | null>(null);

  const needsFarmContactPicker =
    selectedCode === DRONE_PHASE2_TEMPLATE_CODE ||
    selectedCode === PESTICIDE_DRONE_TEMPLATE_CODE ||
    selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE ||
    selectedCode === XLHUB_DRAINAGE_TEMPLATE_CODE ||
    selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE ||
    selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE ||
    selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE;

  const needsContactPicker = selectedCode in TEMPLATE_CONTACT_DEFAULTS;
  const contactSelectionOk =
    !needsContactPicker || showSimon || showVictor || showYannick;

  const applyTemplateContactDefaults = (code: string) => {
    const defaults = TEMPLATE_CONTACT_DEFAULTS[code];
    if (!defaults) return;
    setShowSimon(defaults.simon);
    setShowVictor(defaults.victor);
    setShowYannick(defaults.yannick);
  };

  useEffect(() => {
    applyTemplateContactDefaults(selectedCode);
  }, [selectedCode]);

  const loadSurveyResponses = useCallback(() => {
    setSurveyLoading(true);
    setSurveyLoadError(null);
    listSurveyPresentationResponses({ limit: 500, offset: 0 })
      .then((res) => {
        setSurveyItems(res.items ?? []);
        setSurveyTotal(res.total ?? 0);
      })
      .catch((err: unknown) => {
        setSurveyItems([]);
        setSurveyTotal(0);
        setSurveyLoadError(
          err instanceof Error ? err.message : "Impossible de charger les réponses au sondage"
        );
      })
      .finally(() => setSurveyLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingTemplates(true);
    listEmailTemplates()
      .then((res) => {
        if (!cancelled) {
          let items = res.items ?? [];
          const hasDrs = items.some((t) => t.code === DEFAULT_LIST_TEMPLATE);
          if (!hasDrs) {
            items = [DRS_TEMPLATE_ITEM, ...items];
          }
          setTemplates(
            [...items].sort((a, b) =>
              a.code === DEFAULT_LIST_TEMPLATE ? -1 : b.code === DEFAULT_LIST_TEMPLATE ? 1 : 0
            )
          );
          setSelectedCode(DEFAULT_LIST_TEMPLATE);
          setSubject(DEFAULT_LIST_SUBJECT);
          setFromName(DEFAULT_LIST_FROM_NAME);
          setFromEmail(DEFAULT_LIST_FROM_EMAIL);
          if (items.length && !templateWithFiles) setTemplateWithFiles(items[0].code);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Impossible de charger les templates");
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTab !== "registre") return;
    setRegisterLoading(true);
    listEmailRegister({
      limit: 200,
      offset: 0,
      to_email: registerFilterApplied || undefined,
      date_from: registerDateFrom.trim().slice(0, 10) || undefined,
      date_to: registerDateTo.trim().slice(0, 10) || undefined,
    })
      .then((res) => {
        setRegisterItems(res.items);
        setRegisterTotal(res.total);
      })
      .catch(() => setRegisterItems([]))
      .finally(() => setRegisterLoading(false));
  }, [activeTab, registerFilterApplied, registerDateFrom, registerDateTo]);

  useEffect(() => {
    if (activeTab !== "sondage") return;
    loadSurveyResponses();
  }, [activeTab, loadSurveyResponses]);

  /** À la sélection du template entente de collaboration : expéditeur / CC / sujet alignés (évite les restes DRS). */
  useEffect(() => {
    if (selectedCode !== AMBASSADEUR_TEMPLATE_CODE) return;
    setFromName(AMBASSADEUR_FROM_NAME);
    setFromEmail(AMBASSADEUR_FROM_EMAIL);
    setCcText((prev) => mergeCcWithAmbassadorDefault(prev));
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** Suivi : présentation XLKey (expéditeur Victor Morin). */
  useEffect(() => {
    if (selectedCode !== SUIVI_TEMPLATE_CODE) return;
    setFromName(SUIVI_FROM_NAME);
    setFromEmail(SUIVI_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** Drone Académie Phase 2 : expéditeur XLKey + yannickgoulet@xlkey.ca ; sujet auto côté serveur. */
  useEffect(() => {
    if (selectedCode !== DRONE_PHASE2_TEMPLATE_CODE) return;
    setFromName(DRONE_PHASE2_FROM_NAME);
    setFromEmail(DRONE_PHASE2_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** INFO - Application de pesticides par drone : expéditeur XLKey + info@xlkey.ca ; CC par défaut. */
  useEffect(() => {
    if (selectedCode !== PESTICIDE_DRONE_TEMPLATE_CODE) return;
    setFromName(PESTICIDE_DRONE_FROM_NAME);
    setFromEmail(PESTICIDE_DRONE_FROM_EMAIL);
    setCcText(PESTICIDE_DRONE_CC_DEFAULT);
    setBccText(PESTICIDE_DRONE_BCC_DEFAULT);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** Annonce pulvérisation drone CA : expéditeur XLKey + sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE) return;
    setFromName(ANNONCE_PULVERISATION_DRONE_CA_FROM_NAME);
    setFromEmail(ANNONCE_PULVERISATION_DRONE_CA_FROM_EMAIL);
    setCcText(ANNONCE_PULVERISATION_DRONE_CA_CC_DEFAULT);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
    setContactFormUrl((url) => url.trim() || ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT);
  }, [selectedCode]);

  /** INFO - Référence assurance drone : sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== INFO_REFERENCE_ASSURANCE_DRONE_TEMPLATE_CODE) return;
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** XLHUB — Formats drainage : expéditeur XLKey + info@xlkey.ca ; sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== XLHUB_DRAINAGE_TEMPLATE_CODE) return;
    setFromName(XLHUB_DRAINAGE_FROM_NAME);
    setFromEmail(XLHUB_DRAINAGE_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** Accès JohnDeere : expéditeur XLKey + sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== ACCES_JOHNDEERE_TEMPLATE_CODE) return;
    setFromName(ACCES_JOHNDEERE_FROM_NAME);
    setFromEmail(ACCES_JOHNDEERE_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** Immatriculation drone TC : expéditeur XLKey + sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== DRONE_IMMATRICULATION_TC_TEMPLATE_CODE) return;
    setFromName(DRONE_IMMATRICULATION_TC_FROM_NAME);
    setFromEmail(DRONE_IMMATRICULATION_TC_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
  }, [selectedCode]);

  /** AGRAS : expéditeur XLKey + sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE) return;
    setFromName(AGRAS_FLIGHT_PARAMETERS_FROM_NAME);
    setFromEmail(AGRAS_FLIGHT_PARAMETERS_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
    setAgrasDroneModels((prev) => (prev.length ? prev : AGRAS_DRONE_OPTIONS.map((opt) => opt.id)));
  }, [selectedCode]);

  /** Avis Google XLKey : expéditeur XLKey + sujet par défaut côté serveur. */
  useEffect(() => {
    if (selectedCode !== AVIS_GOOGLE_XLKEY_TEMPLATE_CODE) return;
    setFromName(AVIS_GOOGLE_XLKEY_FROM_NAME);
    setFromEmail(AVIS_GOOGLE_XLKEY_FROM_EMAIL);
    setSubject((s) => (s.trim() === DEFAULT_LIST_SUBJECT ? "" : s));
    setGoogleReviewUrl((url) => url.trim() || AVIS_GOOGLE_XLKEY_DEFAULT_REVIEW_URL);
  }, [selectedCode]);

  /** Charger la liste des fermes (clients) pour les templates pouvant préremplir un destinataire CRM. */
  useEffect(() => {
    if (!needsFarmContactPicker) return;
    let cancelled = false;
    setEmailFarmLoading(true);
    listFarms(1, 500)
      .then((data) => {
        if (cancelled) return;
        const items = (data.items || []).map((row: { organization: { id: string; name: string } }) => ({
          id: row.organization.id,
          name: row.organization.name,
        }));
        setEmailFarmOptions(items.sort((a, b) => a.name.localeCompare(b.name, "fr")));
      })
      .catch(() => {
        if (!cancelled) setEmailFarmOptions([]);
      })
      .finally(() => {
        if (!cancelled) setEmailFarmLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsFarmContactPicker]);

  /** Contacts de la ferme sélectionnée. */
  useEffect(() => {
    if (!needsFarmContactPicker || !selectedFarmIdEmail) {
      setFarmContactsForEmail([]);
      setSelectedContactIdEmail("");
      return;
    }
    let cancelled = false;
    setContactsLoadingForEmail(true);
    listFarmContacts(selectedFarmIdEmail)
      .then((list) => {
        if (!cancelled) setFarmContactsForEmail(list || []);
      })
      .catch(() => {
        if (!cancelled) setFarmContactsForEmail([]);
      })
      .finally(() => {
        if (!cancelled) setContactsLoadingForEmail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsFarmContactPicker, selectedFarmIdEmail]);

  const canSendWithFiles =
    templateWithFiles &&
    (templateWithFiles === "phos" || subjectWithFiles.trim()) &&
    recipientsFile &&
    zipFile &&
    !sendingWithFiles;

  const handleSendWithFiles = () => {
    if (!canSendWithFiles || !recipientsFile || !zipFile) return;
    setError(null);
    setResultWithFiles(null);
    setSendingWithFiles(true);
    sendBulkEmailWithFiles({
      template_code: templateWithFiles,
      subject: subjectWithFiles.trim(),
      from_email: fromEmailWithFiles.trim() || undefined,
      from_name: fromNameWithFiles.trim() || undefined,
      cc: ccWithFiles.trim() || undefined,
      cci: cciWithFiles.trim() || undefined,
      recipients_file: recipientsFile,
      zip_file: zipFile,
    })
      .then((res) => {
        setResultWithFiles(res);
        if (res.sent === res.total && res.errors.length === 0) {
          setRecipientsFile(null);
          setZipFile(null);
          setSubjectWithFiles("");
        }
      })
      .catch((err) => setError(err?.message || "Échec de l'envoi"))
      .finally(() => setSendingWithFiles(false));
  };

  const recipients = parseEmailList(emailListText);
  const isPhosListe = selectedCode === "phos";
  const isAmbListe = selectedCode === AMBASSADEUR_TEMPLATE_CODE;
  const isDronePhase2Liste = selectedCode === DRONE_PHASE2_TEMPLATE_CODE;
  const isSuiviListe = selectedCode === SUIVI_TEMPLATE_CODE;
  const isPesticideDroneListe = selectedCode === PESTICIDE_DRONE_TEMPLATE_CODE;
  const isAnnoncePulverisationDroneCaListe =
    selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE;
  const isInfoReferenceAssuranceDroneListe = selectedCode === INFO_REFERENCE_ASSURANCE_DRONE_TEMPLATE_CODE;
  const isXlhubDrainageListe = selectedCode === XLHUB_DRAINAGE_TEMPLATE_CODE;
  const isAccesJohndeereListe = selectedCode === ACCES_JOHNDEERE_TEMPLATE_CODE;
  const isDroneImmatriculationTcListe = selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE;
  const isAgrasFlightParametersListe = selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE;
  const isAvisGoogleXlkeyListe = selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE;
  const agrasDefaultSubject = buildAgrasDefaultSubject(agrasDroneModels);
  const subjectOkListe = isPhosListe
    ? greetingName.trim().length > 0 || subject.trim().length > 0
    : isAmbListe
      ? true
      : isDronePhase2Liste
        ? true
        : isSuiviListe
          ? true
          : isPesticideDroneListe
            ? true
            : isAnnoncePulverisationDroneCaListe
              ? true
              : isInfoReferenceAssuranceDroneListe
              ? true
              : isXlhubDrainageListe
                ? true
                : isAccesJohndeereListe
                  ? true
                  : isDroneImmatriculationTcListe
                    ? true
                    : isAgrasFlightParametersListe
                      ? true
                      : isAvisGoogleXlkeyListe
                        ? true
                      : subject.trim().length > 0;
  const dronePhase2SlotsOk =
    !isDronePhase2Liste ||
    ((droneFormationMode !== "scheduled" || Boolean(droneFormationLocal.trim())) &&
      (droneRevisionMode !== "scheduled" || Boolean(droneRevisionLocal.trim())));
  const suiviBroadcastOk =
    !isSuiviListe || !surveyBroadcast || recipients.length === 1;
  const agrasSelectionOk = !isAgrasFlightParametersListe || agrasDroneModels.length > 0;
  const canSendAnnonceFromFile =
    isAnnoncePulverisationDroneCaListe && Boolean(annonceBulkRecipientsFile) && !sending;
  const canSendManualList = Boolean(
    selectedCode &&
      subjectOkListe &&
      recipients.length > 0 &&
      !sending &&
      dronePhase2SlotsOk &&
      suiviBroadcastOk &&
      agrasSelectionOk &&
      contactSelectionOk &&
      !(isAnnoncePulverisationDroneCaListe && annonceBulkRecipientsFile)
  );
  const canSend = canSendAnnonceFromFile || canSendManualList;
  const canExportAmbassadeurPdf = Boolean(isAmbListe && !exportingAmbassadeurPdf);

  const buildDronePhase2Payload = () => {
    if (!isDronePhase2Liste) return {};
    const fpDisp =
      droneFormationMode === "scheduled" ? formatFrenchDateTimeFromLocal(droneFormationLocal) : null;
    const rvDisp =
      droneRevisionMode === "scheduled" ? formatFrenchDateTimeFromLocal(droneRevisionLocal) : null;
    const fpSplit = droneFormationMode === "scheduled" ? splitLocalDateTime(droneFormationLocal) : null;
    const rvSplit = droneRevisionMode === "scheduled" ? splitLocalDateTime(droneRevisionLocal) : null;
    return {
      formation_schedule_mode: droneFormationMode,
      revision_schedule_mode: droneRevisionMode,
      venue_schedule_mode: droneVenueMode,
      formation_datetime: fpDisp || null,
      revision_datetime: rvDisp || null,
      venue_address: droneVenueMode === "scheduled" ? droneVenue.trim() || null : null,
      formation_date: fpSplit?.date ?? null,
      formation_time: fpSplit?.time ?? null,
      revision_date: rvSplit?.date ?? null,
      revision_time: rvSplit?.time ?? null,
      participant_brings_exam_drone: droneParticipantBringsDrone,
      participant_brings_safety_equipment: droneParticipantBringsSafety,
    };
  };

  const buildContactPayload = () => {
    if (!needsContactPicker) return {};
    return {
      show_simon: showSimon,
      show_victor: showVictor,
      show_yannick: showYannick,
    };
  };

  const buildDroneImmatriculationTcPayload = () => {
    if (!isDroneImmatriculationTcListe) return {};
    return {
      purchase_date: tcPurchaseDate.trim() || null,
      drone_brand: tcDroneBrand.trim() || null,
      drone_model: tcDroneModel.trim() || null,
      drone_serial_number: tcDroneSerialNumber.trim() || null,
      drone_type: tcDroneType.trim() || null,
    };
  };

  const buildAgrasFlightParametersPayload = () => {
    if (!isAgrasFlightParametersListe) return {};
    return {
      agras_drone_models: agrasDroneModels,
    };
  };

  const getRegisterExportParams = () => ({
    limit: 10000,
    offset: 0,
    to_email: registerFilterApplied || undefined,
    date_from: registerDateFrom.trim().slice(0, 10) || undefined,
    date_to: registerDateTo.trim().slice(0, 10) || undefined,
  });

  const handleExportRegisterCsv = () => {
    setRegisterExporting(true);
    listEmailRegister(getRegisterExportParams())
      .then((res) => {
        const header = REGISTER_EXPORT_COLUMNS.map(csvEscape).join(";");
        const rows = res.items.map((row) => registerRowToExportRow(row).map(csvEscape).join(";"));
        const csv = "\uFEFF" + [header, ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `registre-envois-courriel-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err?.message ?? "Erreur lors de l'export"))
      .finally(() => setRegisterExporting(false));
  };

  const handleExportRegisterExcel = () => {
    setRegisterExporting(true);
    listEmailRegister(getRegisterExportParams())
      .then((res) => {
        const data = [[...REGISTER_EXPORT_COLUMNS], ...res.items.map((row) => registerRowToExportRow(row))];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Registre envois");
        XLSX.writeFile(wb, `registre-envois-courriel-${new Date().toISOString().slice(0, 10)}.xlsx`);
      })
      .catch((err) => setError(err?.message ?? "Erreur lors de l'export"))
      .finally(() => setRegisterExporting(false));
  };

  const handleExportAmbassadeurPdf = () => {
    if (!canExportAmbassadeurPdf) return;
    setError(null);
    setExportingAmbassadeurPdf(true);
    exportAmbassadeurLetterPdf({
      greeting_name: greetingName.trim() || undefined,
      confirmation_name: confirmationName.trim() || undefined,
      subject: subject.trim() || undefined,
    })
      .then(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err?.message || "Impossible d'exporter le PDF"))
      .finally(() => setExportingAmbassadeurPdf(false));
  };

  const handleSend = () => {
    if (!canSend) return;
    setError(null);
    setResult(null);

    const contactFormPayload =
      selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE
        ? (contactFormUrl.trim() || undefined)
        : undefined;
    const contactExtra = buildContactPayload();

    if (isAnnoncePulverisationDroneCaListe && annonceBulkRecipientsFile) {
      setSending(true);
      sendBulkEmailFromFile({
        template_code: ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE,
        subject: subject.trim(),
        from_name: fromName.trim() || ANNONCE_PULVERISATION_DRONE_CA_FROM_NAME,
        from_email: fromEmail.trim() || ANNONCE_PULVERISATION_DRONE_CA_FROM_EMAIL,
        cc: ccText.trim() || ANNONCE_PULVERISATION_DRONE_CA_CC_DEFAULT,
        cci: bccText.trim() || undefined,
        contact_form_url: contactFormPayload,
        ...contactExtra,
        recipients_file: annonceBulkRecipientsFile,
      })
        .then((res) => {
          setResult(res);
          if (res.sent === res.total && res.errors.length === 0) {
            setAnnonceBulkRecipientsFile(null);
            setSubject("");
          }
        })
        .catch((err) => setError(err?.message || "Échec de l'envoi en masse"))
        .finally(() => setSending(false));
      return;
    }

    setSending(true);
    const ccList = parseEmailList(ccText);
    const bccList = parseEmailList(bccText);
    const greeting = greetingName.trim() || undefined;
    const confirmation =
      selectedCode === AMBASSADEUR_TEMPLATE_CODE ? confirmationName.trim() || undefined : undefined;
    const isDrs = selectedCode === DEFAULT_LIST_TEMPLATE;
    const bodyExtra = isDrs ? (bodyText.trim() || undefined) : undefined;
    const bodyTextPayload = isDrs ? undefined : (bodyText.trim() || undefined);
    const droneExtra = buildDronePhase2Payload();
    const droneTcExtra = buildDroneImmatriculationTcPayload();
    const agrasExtra = buildAgrasFlightParametersPayload();
    const bookingPayload =
      selectedCode === SUIVI_TEMPLATE_CODE ? (bookingUrl.trim() || undefined) : undefined;
    const googleReviewPayload =
      selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE
        ? (googleReviewUrl.trim() || undefined)
        : undefined;
    const suiviBroadcastPayload =
      selectedCode === SUIVI_TEMPLATE_CODE && surveyBroadcast ? true : undefined;

    if (attachedFiles.length > 0) {
      let didFallbackNoAttachments = false;
      sendBulkEmailListWithAttachments({
        template_code: selectedCode,
        subject: subject.trim(),
        from_name: fromName.trim() || undefined,
        from_email: fromEmail.trim() || undefined,
        greeting_name: greeting,
        confirmation_name: confirmation,
        body_extra_after_button: bodyExtra,
        recipients: emailListText,
        cc: ccText.trim() || undefined,
        bcc: bccText.trim() || undefined,
        files: attachedFiles,
        ...droneExtra,
        ...droneTcExtra,
        ...agrasExtra,
        ...contactExtra,
        booking_url: bookingPayload,
        contact_form_url: contactFormPayload,
        google_review_url: googleReviewPayload,
        survey_broadcast: suiviBroadcastPayload,
      })
        .then((res) => {
          setResult(res);
          if (res.sent === res.total && res.errors.length === 0) {
            setEmailListText("");
            setSubject("");
            setBodyText("");
            setAttachedFiles([]);
            if (selectedCode === AMBASSADEUR_TEMPLATE_CODE) setConfirmationName("");
            if (
              selectedCode === DRONE_PHASE2_TEMPLATE_CODE ||
              selectedCode === PESTICIDE_DRONE_TEMPLATE_CODE ||
              selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE ||
              selectedCode === XLHUB_DRAINAGE_TEMPLATE_CODE ||
              selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE ||
              selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE ||
              selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE
            ) {
              if (selectedCode === DRONE_PHASE2_TEMPLATE_CODE) {
                setDroneFormationLocal("");
                setDroneRevisionLocal("");
                setDroneVenue("");
              }
              setSelectedFarmIdEmail("");
              setSelectedContactIdEmail("");
              if (selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE) {
                setTcPurchaseDate("");
                setTcDroneBrand("");
                setTcDroneModel("");
                setTcDroneSerialNumber("");
                setTcDroneType("");
              }
              if (selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE) {
                setAgrasDroneModels(AGRAS_DRONE_OPTIONS.map((opt) => opt.id));
              }
            }
            if (selectedCode === SUIVI_TEMPLATE_CODE) {
              setBookingUrl("");
              setSurveyBroadcast(false);
            }
            if (selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE) {
              setContactFormUrl(ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT);
            }
          }
        })
        .catch((err: Error & { status?: number }) => {
          const is404 =
            err?.status === 404 ||
            (err?.message && (err.message.includes("Not Found") || err.message.includes("404")));
          if (is404) {
            didFallbackNoAttachments = true;
            setError(
              "L'envoi avec pièces jointes n'est pas disponible sur ce serveur. Envoi sans pièces jointes…"
            );
            sendBulkEmail({
              template_code: selectedCode,
              recipients,
              subject: subject.trim(),
              body_text: bodyTextPayload,
              body_html: undefined,
              from_name: fromName,
              from_email: fromEmail.trim() || undefined,
              cc: ccList.length > 0 ? ccList : undefined,
              bcc: bccList.length > 0 ? bccList : undefined,
              greeting_name: greeting,
              confirmation_name: confirmation,
              body_extra_after_button: bodyExtra,
              ...droneExtra,
              ...droneTcExtra,
              ...agrasExtra,
              booking_url: bookingPayload,
              contact_form_url: contactFormPayload,
              google_review_url: googleReviewPayload,
              survey_broadcast: suiviBroadcastPayload,
            })
              .then((res) => {
                setResult(res);
                setError(
                  "Message envoyé sans les pièces jointes (serveur à mettre à jour). Pour activer les pièces jointes, redéployez le backend."
                );
                if (res.sent === res.total && res.errors.length === 0) {
                  setEmailListText("");
                  setSubject("");
                  setBodyText("");
                  setAttachedFiles([]);
                  if (selectedCode === AMBASSADEUR_TEMPLATE_CODE) setConfirmationName("");
                  if (
                    selectedCode === DRONE_PHASE2_TEMPLATE_CODE ||
                    selectedCode === PESTICIDE_DRONE_TEMPLATE_CODE ||
                    selectedCode === XLHUB_DRAINAGE_TEMPLATE_CODE ||
                    selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE ||
                    selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE ||
                    selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE
                  ) {
                    if (selectedCode === DRONE_PHASE2_TEMPLATE_CODE) {
                      setDroneFormationLocal("");
                      setDroneRevisionLocal("");
                      setDroneVenue("");
                    }
                    setSelectedFarmIdEmail("");
                    setSelectedContactIdEmail("");
                    if (selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE) {
                      setTcPurchaseDate("");
                      setTcDroneBrand("");
                      setTcDroneModel("");
                      setTcDroneSerialNumber("");
                      setTcDroneType("");
                    }
                    if (selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE) {
                      setAgrasDroneModels(AGRAS_DRONE_OPTIONS.map((opt) => opt.id));
                    }
                  }
                  if (selectedCode === SUIVI_TEMPLATE_CODE) {
                    setBookingUrl("");
                    setSurveyBroadcast(false);
                  }
                }
              })
              .catch((e) => {
                const msg = (e as Error)?.message || "Échec de l'envoi";
                setError(msg.includes("Template inconnu") ? TEMPLATE_INCONNU_MSG : msg);
              })
              .finally(() => setSending(false));
            return;
          }
          const errMsg = err?.message || "Échec de l'envoi";
          setError(errMsg.includes("Template inconnu") ? TEMPLATE_INCONNU_MSG : errMsg);
          setSending(false);
        })
        .finally(() => {
          if (!didFallbackNoAttachments) setSending(false);
        });
    } else {
      sendBulkEmail({
        template_code: selectedCode,
        recipients,
        subject: subject.trim(),
        body_text: bodyTextPayload,
        body_html: undefined,
        from_name: fromName,
        from_email: fromEmail.trim() || undefined,
        cc: ccList.length > 0 ? ccList : undefined,
        bcc: bccList.length > 0 ? bccList : undefined,
        greeting_name: greeting,
        confirmation_name: confirmation,
        body_extra_after_button: bodyExtra,
        ...droneExtra,
        ...droneTcExtra,
        ...agrasExtra,
        ...contactExtra,
        booking_url: bookingPayload,
        contact_form_url: contactFormPayload,
        google_review_url: googleReviewPayload,
        survey_broadcast: suiviBroadcastPayload,
      })
        .then((res) => {
          setResult(res);
          if (res.sent === res.total && res.errors.length === 0) {
            setEmailListText("");
            setSubject("");
            setBodyText("");
            if (selectedCode === AMBASSADEUR_TEMPLATE_CODE) setConfirmationName("");
            if (
              selectedCode === DRONE_PHASE2_TEMPLATE_CODE ||
              selectedCode === PESTICIDE_DRONE_TEMPLATE_CODE ||
              selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE ||
              selectedCode === XLHUB_DRAINAGE_TEMPLATE_CODE ||
              selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE ||
              selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE ||
              selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE
            ) {
              if (selectedCode === DRONE_PHASE2_TEMPLATE_CODE) {
                setDroneFormationLocal("");
                setDroneRevisionLocal("");
                setDroneVenue("");
              }
              setSelectedFarmIdEmail("");
              setSelectedContactIdEmail("");
              if (selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE) {
                setTcPurchaseDate("");
                setTcDroneBrand("");
                setTcDroneModel("");
                setTcDroneSerialNumber("");
                setTcDroneType("");
              }
              if (selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE) {
                setAgrasDroneModels(AGRAS_DRONE_OPTIONS.map((opt) => opt.id));
              }
            }
            if (selectedCode === SUIVI_TEMPLATE_CODE) {
              setBookingUrl("");
              setSurveyBroadcast(false);
            }
            if (selectedCode === ANNONCE_PULVERISATION_DRONE_CA_TEMPLATE_CODE) {
              setContactFormUrl(ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT);
            }
          }
        })
        .catch((err) => {
        const msg = (err as Error)?.message || "Échec de l'envoi";
        setError(msg.includes("Template inconnu") ? TEMPLATE_INCONNU_MSG : msg);
      })
        .finally(() => setSending(false));
    }
  };

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Commercial — Envoi de courriel</div>}
      userName={userName}
      modules={commercialModules}
      userMenuItems={[
        { label: "Profil", onClick: () => (window.location.hash = "#/profile") },
        { label: "Paramètres", onClick: () => (window.location.hash = "#/settings") },
        { label: "Déconnexion", onClick: () => {} },
      ]}
    >
      <div className="space-y-6 max-w-6xl">
        <div>
          <h1 className="text-section text-text flex items-center gap-2">
            <Mail size={24} />
            Envoi de courriel
          </h1>
          <p className="text-sm text-muted mt-1">
            Choisissez un template, saisissez la liste des adresses (une par ligne, ou {EMAIL_ADDRESSES_SEPARATOR_HINT.toLowerCase()}). Chaque destinataire recoit le courriel individuellement (pas en CCI).
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger" role="alert">
            <p className="font-medium">Erreur lors de l&apos;envoi</p>
            <p className="mt-1 whitespace-pre-wrap break-words">{error}</p>
          </div>
        )}

        <div className="flex gap-2 border-b border-border pb-2">
          <button
            type="button"
            onClick={() => setActiveTab("liste")}
            className={cn(
              "px-4 py-2 rounded-t text-sm font-medium",
              activeTab === "liste"
                ? "bg-surface border border-border border-b-0 -mb-px text-text"
                : "text-muted hover:text-text"
            )}
          >
            Liste
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("fichier")}
            className={cn(
              "px-4 py-2 rounded-t text-sm font-medium flex items-center gap-1",
              activeTab === "fichier"
                ? "bg-surface border border-border border-b-0 -mb-px text-text"
                : "text-muted hover:text-text"
            )}
          >
            <FileUp size={16} />
            Avec Fichier
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("registre")}
            className={cn(
              "px-4 py-2 rounded-t text-sm font-medium flex items-center gap-1",
              activeTab === "registre"
                ? "bg-surface border border-border border-b-0 -mb-px text-text"
                : "text-muted hover:text-text"
            )}
          >
            <BookOpen size={16} />
            Registre
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("sondage")}
            className={cn(
              "px-4 py-2 rounded-t text-sm font-medium flex items-center gap-1",
              activeTab === "sondage"
                ? "bg-surface border border-border border-b-0 -mb-px text-text"
                : "text-muted hover:text-text"
            )}
          >
            <ClipboardList size={16} />
            Sondage
          </button>
        </div>

        {activeTab === "liste" && (
        <div className="rounded-lg border border-border bg-surface p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Modèle</label>
            <select
              value={selectedCode}
              onChange={(e) => setSelectedCode(e.target.value)}
              disabled={loadingTemplates}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            >
              {loadingTemplates ? (
                <option>Chargement...</option>
              ) : (
                templates.map((t) => (
                  <option key={t.code} value={t.code}>
                    {formatTemplateOptionLabel(t)}
                  </option>
                ))
              )}
            </select>
            <p className="mt-1 text-xs text-muted">
              {isDronePhase2Liste
                ? "Drone Académie Phase 2 : expéditeur XLKey et yannickgoulet@xlkey.ca ; configurez séparément la formation pratique, la révision en vol et le lieu (ne pas mentionner, à déterminer, ou dates précises) ; le sujet est généré automatiquement (nom depuis la salutation) si le champ Sujet est vide."
                : isXlhubDrainageListe
                  ? "XLHUB — Formats de données de drainage : expéditeur XLKey et info@xlkey.ca ; choix d’une ferme et d’un contact CRM pour préremplir le prénom (salutation « Bonjour … ») et le courriel. Sujet par défaut côté serveur si le champ Sujet est vide."
                : isPesticideDroneListe
                  ? "INFO - Application de pesticides par drone : expéditeur XLKey et info@xlkey.ca ; CC et CCI pré-remplis (modifiables). Choix d’une ferme et d’un contact CRM (optionnel) pour préremplir salutation et destinataire. Sujet par défaut côté serveur si le champ Sujet est vide."
                : isAnnoncePulverisationDroneCaListe
                  ? "Annonce officielle — pulvérisation par drone au Canada : expéditeur XLKey et info@xlkey.ca ; bouton vers le formulaire de contact ; contacts Victor Morin et Simon Carrier. Sujet par défaut côté serveur si le champ Sujet est vide."
                : isAgrasFlightParametersListe
                  ? "INFO - Paramètres de vol AGRAS : expéditeur XLKey et info@xlkey.ca ; PDF joint automatiquement et lien public vers la version web. Sujet par défaut côté serveur si le champ Sujet est vide."
                : isAvisGoogleXlkeyListe
                  ? "Avis Google : invitation professionnelle à laisser un avis 5 étoiles ; expéditeur XLKey et info@xlkey.ca ; choix d’une ferme et d’un contact CRM pour préremplir le prénom. Sujet par défaut côté serveur si le champ Sujet est vide."
                : isAmbListe
                  ? "Entente de collaboration (ambassadeur) : expéditeur XLKey (victormorin@xlkey.ca) et CC appliqués automatiquement côté serveur ; le formulaire est pré-rempli à la sélection."
                  : isSuiviListe
                    ? "Suivi : chaque destinataire reçoit un lien de sondage personnel et sécurisé. Sujet par défaut côté serveur si le champ Sujet est vide."
                  : "Par défaut : PHOS - DRS FEB 27 2026 (sujet et expéditeur pré-remplis)."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Nom de l'expéditeur</label>
            <select
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              disabled={
                isAmbListe ||
                isDronePhase2Liste ||
                isSuiviListe ||
                isPesticideDroneListe ||
                isAnnoncePulverisationDroneCaListe ||
                isXlhubDrainageListe ||
                isAccesJohndeereListe ||
                isAgrasFlightParametersListe ||
                isAvisGoogleXlkeyListe
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text disabled:opacity-70"
            >
              <option value="XLFlow">XLFlow</option>
              <option value="XLKey">XLKey</option>
              <option value="First Phosphate Corp.">First Phosphate Corp.</option>
              <option value={SUIVI_FROM_NAME}>{SUIVI_FROM_NAME}</option>
            </select>
            <p className="mt-1 text-xs text-muted">
              {isDronePhase2Liste
                ? "Fixé à XLKey pour ce template (non modifiable)."
                : isPesticideDroneListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                : isAnnoncePulverisationDroneCaListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                : isXlhubDrainageListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                : isAccesJohndeereListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                : isAvisGoogleXlkeyListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                : isAgrasFlightParametersListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                : isAmbListe
                  ? "Fixé à XLKey pour ce template (non modifiable)."
                  : isSuiviListe
                    ? "Fixé à Victor Morin pour ce template (non modifiable)."
                  : "Nom affiché dans la boîte de réception du destinataire."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Adresse expéditrice (optionnel)</label>
            <input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              readOnly={
                isAmbListe ||
                isDronePhase2Liste ||
                isSuiviListe ||
                isPesticideDroneListe ||
                isAnnoncePulverisationDroneCaListe ||
                isXlhubDrainageListe ||
                isAccesJohndeereListe ||
                isAgrasFlightParametersListe ||
                isAvisGoogleXlkeyListe
              }
              placeholder={
                isDronePhase2Liste
                  ? DRONE_PHASE2_FROM_EMAIL
                  : isPesticideDroneListe
                    ? PESTICIDE_DRONE_FROM_EMAIL
                  : isAnnoncePulverisationDroneCaListe
                    ? ANNONCE_PULVERISATION_DRONE_CA_FROM_EMAIL
                  : isXlhubDrainageListe
                    ? XLHUB_DRAINAGE_FROM_EMAIL
                  : isAccesJohndeereListe
                    ? ACCES_JOHNDEERE_FROM_EMAIL
                  : isAvisGoogleXlkeyListe
                    ? AVIS_GOOGLE_XLKEY_FROM_EMAIL
                  : isAgrasFlightParametersListe
                    ? AGRAS_FLIGHT_PARAMETERS_FROM_EMAIL
                  : isAmbListe
                    ? AMBASSADEUR_FROM_EMAIL
                    : isSuiviListe
                      ? SUIVI_FROM_EMAIL
                    : "Ex: bennett@firstphosphate.com — laisser vide pour l'adresse par défaut (info@xlkey.ca)"
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text read-only:bg-muted/30"
            />
            <p className="mt-1 text-xs text-muted">
              {isDronePhase2Liste
                ? `Fixée à ${DRONE_PHASE2_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isPesticideDroneListe
                  ? `Fixée à ${PESTICIDE_DRONE_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isAnnoncePulverisationDroneCaListe
                  ? `Fixée à ${ANNONCE_PULVERISATION_DRONE_CA_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isXlhubDrainageListe
                  ? `Fixée à ${XLHUB_DRAINAGE_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isAccesJohndeereListe
                  ? `Fixée à ${ACCES_JOHNDEERE_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isAvisGoogleXlkeyListe
                  ? `Fixée à ${AVIS_GOOGLE_XLKEY_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isAgrasFlightParametersListe
                  ? `Fixée à ${AGRAS_FLIGHT_PARAMETERS_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                : isAmbListe
                  ? "Fixée à victormorin@xlkey.ca pour ce template (non modifiable dans l’interface)."
                  : isSuiviListe
                    ? `Fixée à ${SUIVI_FROM_EMAIL} pour ce template (non modifiable dans l’interface).`
                  : "Doit être vérifiée dans AWS SES si vous utilisez SES."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">CC (optionnel)</label>
            <input
              type="text"
              value={ccText}
              onChange={(e) => setCcText(e.target.value)}
              placeholder={
                isPesticideDroneListe
                  ? PESTICIDE_DRONE_CC_DEFAULT
                  : isAnnoncePulverisationDroneCaListe
                    ? ANNONCE_PULVERISATION_DRONE_CA_CC_DEFAULT
                  : isAmbListe
                    ? `${AMBASSADEUR_FROM_EMAIL} — vous pouvez ajouter d’autres adresses`
                  : `Ex: a@exemple.com b@exemple.com — ${EMAIL_ADDRESSES_SEPARATOR_HINT.toLowerCase()}`
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            />
            {!isAmbListe && !isPesticideDroneListe && !isAnnoncePulverisationDroneCaListe && (
              <p className="mt-1 text-xs text-muted">{EMAIL_ADDRESSES_SEPARATOR_HINT}.</p>
            )}
            {isAmbListe && (
              <p className="mt-1 text-xs text-muted">
                victormorin@xlkey.ca est toujours en copie (fusion sans doublon avec la liste ci-dessus).
              </p>
            )}
            {isPesticideDroneListe && (
              <p className="mt-1 text-xs text-muted">
                CC (modifiable) : yannickgoulet@xlkey.ca, simoncarrier@xlkey.ca, info@xlkey.ca — CCI (modifiable) :
                victormorin@xlkey.ca.
              </p>
            )}
            {isAnnoncePulverisationDroneCaListe && (
              <p className="mt-1 text-xs text-muted">
                {ANNONCE_PULVERISATION_DRONE_CA_CC_DEFAULT} est toujours en copie (fusion sans doublon avec la liste ci-dessus).
              </p>
            )}
          </div>

          {isPesticideDroneListe && (
            <div>
              <label className="block text-sm font-medium text-text mb-1">CCI (copie invisible)</label>
              <input
                type="text"
                value={bccText}
                onChange={(e) => setBccText(e.target.value)}
                placeholder={PESTICIDE_DRONE_BCC_DEFAULT}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Les adresses en CCI reçoivent le courriel sans apparaître aux autres destinataires (modifiable).{" "}
                {EMAIL_ADDRESSES_SEPARATOR_HINT}.
              </p>
            </div>
          )}

          {isSuiviListe && (
            <div>
              <label className="block text-sm font-medium text-text mb-1">Lien de prise de rendez-vous</label>
              <input
                type="url"
                value={bookingUrl}
                onChange={(e) => setBookingUrl(e.target.value)}
                placeholder="https://… (Calendly ou autre)"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Affiché comme bouton dans le courriel. Si vide, la valeur par défaut serveur (
                <code className="text-xs">presentation_xlkey_default_booking_url</code>) est utilisée si configurée.
              </p>
              <label className="mt-3 flex items-start gap-2 cursor-pointer text-sm text-text">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-border"
                  checked={surveyBroadcast}
                  onChange={(e) => setSurveyBroadcast(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Lien de sondage partageable</span> — pour un relais qui transmettra le
                  courriel à plusieurs personnes. Indiquez <strong>une seule</strong> adresse : le même lien acceptera
                  plusieurs réponses (une par envoi). Incompatible avec une liste de plusieurs destinataires.
                </span>
              </label>
            </div>
          )}

          {isAnnoncePulverisationDroneCaListe && (
            <div>
              <label className="block text-sm font-medium text-text mb-1">Lien du formulaire de contact</label>
              <input
                type="url"
                value={contactFormUrl}
                onChange={(e) => setContactFormUrl(e.target.value)}
                placeholder={ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Utilisé pour le bouton « Contactez-nous » dans le courriel. Si vide, le lien par défaut (
                {ANNONCE_PULVERISATION_DRONE_CA_CONTACT_FORM_DEFAULT}) est utilisé côté serveur.
              </p>
              <div className="mt-4 rounded-md border border-border bg-muted/20 p-4 space-y-3">
                <p className="text-sm font-medium text-text">Envoi en masse (CSV ou Excel)</p>
                <p className="text-xs text-muted">
                  Colonnes requises : <strong>courriel</strong> (ou email). Optionnel : <strong>prénom</strong> pour
                  personnaliser la salutation (« Bonjour Marie, » — sinon « Bonjour, »). Formats : .csv (séparateur ; ou ,)
                  ou .xlsx.
                </p>
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx"
                  onChange={(e) => setAnnonceBulkRecipientsFile(e.target.files?.[0] ?? null)}
                  className="text-sm text-text file:mr-2 file:py-1 file:px-3 file:rounded file:border file:border-border file:bg-surface file:text-sm"
                />
                {annonceBulkRecipientsFile && (
                  <p className="text-xs text-muted">
                    Fichier sélectionné : <strong>{annonceBulkRecipientsFile.name}</strong>. Cliquez « Envoyer à tous »
                    pour lancer l&apos;envoi personnalisé (un courriel par ligne).
                  </p>
                )}
              </div>
            </div>
          )}

          {isAvisGoogleXlkeyListe && (
            <div>
              <label className="block text-sm font-medium text-text mb-1">Lien « Laisser un avis » Google</label>
              <input
                type="url"
                value={googleReviewUrl}
                onChange={(e) => setGoogleReviewUrl(e.target.value)}
                placeholder={AVIS_GOOGLE_XLKEY_DEFAULT_REVIEW_URL}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Utilisé pour le bouton « Laisser un avis sur Google ». Par défaut :{" "}
                <strong>{AVIS_GOOGLE_XLKEY_DEFAULT_REVIEW_URL}</strong> (modifiable ou via{" "}
                <strong>GOOGLE_REVIEW_URL</strong> dans .env).
              </p>
            </div>
          )}

          {needsFarmContactPicker && (
            <div className="rounded-md border border-border bg-muted/20 p-4 space-y-4">
              <p className="text-sm font-medium text-text">
                {isDronePhase2Liste
                  ? "Drone Académie — Phase 2 : ferme, contact et horaires"
                  : isAnnoncePulverisationDroneCaListe
                    ? "Annonce pulvérisation drone : destinataire CRM (optionnel)"
                  : isXlhubDrainageListe
                    ? "XLHUB — Formats drainage : ferme et contact (CRM)"
                    : isDroneImmatriculationTcListe
                      ? "Immatriculation drone TC : destinataire CRM"
                      : isAgrasFlightParametersListe
                        ? "AGRAS — paramètres de vol : destinataire CRM"
                      : isAvisGoogleXlkeyListe
                        ? "Avis Google : destinataire CRM (optionnel)"
                      : "Ferme et contact (CRM)"}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Ferme / client</label>
                  <select
                    value={selectedFarmIdEmail}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedFarmIdEmail(v);
                      setSelectedContactIdEmail("");
                    }}
                    disabled={emailFarmLoading}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                  >
                    <option value="">{emailFarmLoading ? "Chargement…" : "— Choisir une ferme —"}</option>
                    {emailFarmOptions.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Contact de la ferme</label>
                  <select
                    value={selectedContactIdEmail}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedContactIdEmail(id);
                      const c = farmContactsForEmail.find((x) => x.id === id);
                      if (c) {
                        const nom = `${c.person.first_name} ${c.person.last_name}`.trim();
                        const greet =
                          selectedCode === XLHUB_DRAINAGE_TEMPLATE_CODE ||
                          selectedCode === DRONE_IMMATRICULATION_TC_TEMPLATE_CODE ||
                          selectedCode === AGRAS_FLIGHT_PARAMETERS_TEMPLATE_CODE ||
                          selectedCode === AVIS_GOOGLE_XLKEY_TEMPLATE_CODE
                            ? (c.person.first_name || "").trim()
                            : nom;
                        setGreetingName(greet);
                        if (c.person.email?.trim()) setEmailListText(c.person.email.trim());
                      }
                    }}
                    disabled={!selectedFarmIdEmail || contactsLoadingForEmail}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                  >
                    <option value="">
                      {!selectedFarmIdEmail
                        ? "— Choisir une ferme d’abord —"
                        : contactsLoadingForEmail
                          ? "Chargement…"
                          : "— Choisir un contact —"}
                    </option>
                    {farmContactsForEmail.map((c) => (
                      <option key={c.id} value={c.id}>
                        {`${c.person.first_name} ${c.person.last_name}`.trim()}
                        {c.person.email ? ` · ${c.person.email}` : " (sans courriel)"}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted">
                    {isXlhubDrainageListe || isDroneImmatriculationTcListe || isAgrasFlightParametersListe || isAvisGoogleXlkeyListe
                      ? "Remplit le prénom pour « Bonjour … » et le courriel du destinataire (liste ci-dessous)."
                      : "Remplit le nom pour la salutation et le courriel du destinataire (liste ci-dessous)."}
                  </p>
                </div>
              </div>
              {isDronePhase2Liste && (
                <div className="space-y-5">
                  <p className="text-sm text-muted">
                    Pour la formation pratique, la révision en vol et le lieu, choisissez séparément de ne pas
                    les mentionner, d&apos;indiquer « À déterminer », ou de préciser date/heure et adresse. Le
                    lien calendrier (.ics) n&apos;apparaît que si les deux activités et le lieu sont en mode
                    « précis » avec des dates complètes.
                  </p>
                  <div className="grid gap-5 lg:grid-cols-2">
                    <div className="rounded-md border border-border bg-surface p-4 space-y-3">
                      <p className="text-sm font-medium text-text">Formation pratique</p>
                      <div className="flex flex-col gap-2 text-sm text-text">
                        {DRONE_SCHEDULE_MODE_OPTIONS.map((opt) => (
                          <label key={`fp-${opt.value}`} className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="droneFormationMode"
                              value={opt.value}
                              checked={droneFormationMode === opt.value}
                              onChange={() => setDroneFormationMode(opt.value)}
                              className="border-border"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                      {droneFormationMode === "scheduled" && (
                        <input
                          type="datetime-local"
                          value={droneFormationLocal}
                          onChange={(e) => setDroneFormationLocal(e.target.value)}
                          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                        />
                      )}
                    </div>
                    <div className="rounded-md border border-border bg-surface p-4 space-y-3">
                      <p className="text-sm font-medium text-text">Révision en vol (examen)</p>
                      <div className="flex flex-col gap-2 text-sm text-text">
                        {DRONE_SCHEDULE_MODE_OPTIONS.map((opt) => (
                          <label key={`rv-${opt.value}`} className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="droneRevisionMode"
                              value={opt.value}
                              checked={droneRevisionMode === opt.value}
                              onChange={() => setDroneRevisionMode(opt.value)}
                              className="border-border"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                      {droneRevisionMode === "scheduled" && (
                        <input
                          type="datetime-local"
                          value={droneRevisionLocal}
                          onChange={(e) => setDroneRevisionLocal(e.target.value)}
                          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                        />
                      )}
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-surface p-4 space-y-3">
                    <p className="text-sm font-medium text-text">Lieu</p>
                    <div className="flex flex-col gap-2 text-sm text-text sm:flex-row sm:flex-wrap sm:gap-6">
                      {DRONE_VENUE_MODE_OPTIONS.map((opt) => (
                        <label key={`venue-${opt.value}`} className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="droneVenueMode"
                            value={opt.value}
                            checked={droneVenueMode === opt.value}
                            onChange={() => setDroneVenueMode(opt.value)}
                            className="border-border"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                    {droneVenueMode === "scheduled" && (
                      <input
                        type="text"
                        value={droneVenue}
                        onChange={(e) => setDroneVenue(e.target.value)}
                        placeholder="Par défaut : adresse XLKey Sainte-Marie si vide"
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      />
                    )}
                  </div>
                  {!dronePhase2SlotsOk && (
                    <p className="text-xs text-danger">
                      Indiquez la date et l&apos;heure pour chaque activité en mode « Date et heure précises ».
                    </p>
                  )}
                  <div className="rounded-md border border-border bg-surface p-4 space-y-3">
                    <p className="text-sm font-medium text-text">Matériel pour la révision en vol</p>
                    <p className="text-xs text-muted">
                      Par défaut, XLKey fournit le drone (DJI Mavic 3M) et l&apos;équipement de sécurité sur place.
                      Cochez ci-dessous pour déplacer un élément dans la liste « Vous devez apporter ».
                    </p>
                    <label className="inline-flex items-start gap-2 text-sm text-text">
                      <input
                        type="checkbox"
                        checked={droneParticipantBringsDrone}
                        onChange={(e) => setDroneParticipantBringsDrone(e.target.checked)}
                        className="mt-0.5 rounded border-border"
                      />
                      <span>
                        Le participant apporte son propre drone immatriculé (250 g et plus) — sans modèle précisé
                        dans le courriel
                      </span>
                    </label>
                    <label className="inline-flex items-start gap-2 text-sm text-text">
                      <input
                        type="checkbox"
                        checked={droneParticipantBringsSafety}
                        onChange={(e) => setDroneParticipantBringsSafety(e.target.checked)}
                        className="mt-0.5 rounded border-border"
                      />
                      <span>
                        Le participant apporte l&apos;équipement de sécurité (extincteur, cônes, trousse de premiers
                        soins, etc.)
                      </span>
                    </label>
                  </div>
                </div>
              )}
              {isDroneImmatriculationTcListe && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-text">Infos drone (optionnel)</p>
                    <p className="mt-1 text-xs text-muted">
                      Ces champs s'affichent dans le courriel seulement lorsqu'ils sont remplis.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Date d'achat</label>
                      <input
                        type="date"
                        value={tcPurchaseDate}
                        onChange={(e) => setTcPurchaseDate(e.target.value)}
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Marque</label>
                      <input
                        type="text"
                        value={tcDroneBrand}
                        onChange={(e) => setTcDroneBrand(e.target.value)}
                        placeholder="ex: DJI"
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Modèle</label>
                      <input
                        type="text"
                        value={tcDroneModel}
                        onChange={(e) => setTcDroneModel(e.target.value)}
                        placeholder="ex: Agras T50"
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Numéro de série</label>
                      <input
                        type="text"
                        value={tcDroneSerialNumber}
                        onChange={(e) => setTcDroneSerialNumber(e.target.value)}
                        placeholder="ex: 1ZNBJ9D001234"
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Type de drone</label>
                      <select
                        value={tcDroneType}
                        onChange={(e) => setTcDroneType(e.target.value)}
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      >
                        <option value="">— Non précisé —</option>
                        <option value="Drone agricole">Drone agricole</option>
                        <option value="Drone de cartographie">Drone de cartographie</option>
                        <option value="Drone de livraison">Drone de livraison</option>
                        <option value="Drone de surveillance">Drone de surveillance</option>
                        <option value="Drone de photographie">Drone de photographie</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {isAgrasFlightParametersListe && (
                <div className="rounded-md border border-border bg-surface p-4">
                  <p className="text-sm font-medium text-text">Modèles AGRAS inclus</p>
                  <p className="mt-1 text-xs text-muted">
                    Le courriel et la page web publique seront filtrés selon les modèles cochés.
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {AGRAS_DRONE_OPTIONS.map((opt) => (
                      <label key={opt.id} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-text">
                        <input
                          type="checkbox"
                          checked={agrasDroneModels.includes(opt.id)}
                          onChange={(e) => {
                            setAgrasDroneModels((prev) => {
                              if (e.target.checked) {
                                return prev.includes(opt.id) ? prev : [...prev, opt.id];
                              }
                              return prev.filter((id) => id !== opt.id);
                            });
                          }}
                          className="rounded border-border"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {!agrasSelectionOk && (
                    <p className="mt-2 text-xs text-danger">Sélectionnez au moins un modèle AGRAS.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {needsContactPicker && (
            <div className="rounded-md border border-border bg-surface p-4">
              <p className="text-sm font-medium text-text">Contacts affichés dans le courriel</p>
              <p className="mt-1 text-xs text-muted">
                Choisissez un ou plusieurs contacts XLKey (Simon, Victor, Yannick) à inclure dans le bloc
                d&apos;aide ou la signature du courriel.
              </p>
              <div className="mt-3 flex flex-col gap-2 text-sm text-text sm:flex-row sm:flex-wrap sm:gap-6">
                {XLKEY_CONTACT_OPTIONS.map((opt) => (
                  <label key={opt.id} className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={
                        opt.id === "simon"
                          ? showSimon
                          : opt.id === "victor"
                            ? showVictor
                            : showYannick
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (opt.id === "simon") setShowSimon(checked);
                        else if (opt.id === "victor") setShowVictor(checked);
                        else setShowYannick(checked);
                      }}
                      className="rounded border-border"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {!contactSelectionOk && (
                <p className="mt-2 text-xs text-danger">Sélectionnez au moins un contact.</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text mb-1">Nom pour la salutation (optionnel)</label>
            <input
              type="text"
              value={greetingName}
              onChange={(e) => setGreetingName(e.target.value)}
              placeholder={
                isDronePhase2Liste
                  ? "Prénom et nom — utilisé dans « Bonjour … » et dans le sujet automatique (ex: Marie Tremblay)"
                  : isPesticideDroneListe
                    ? "Prénom et nom — rempli via le contact CRM (ex: Salut Marie Tremblay,)"
                  : isXlhubDrainageListe
                    ? "Prénom — rempli via le contact CRM (ex: Bonjour Marie,)"
                    : isDroneImmatriculationTcListe
                      ? "Prénom — rempli via le contact CRM (ex: Salut Marie,)"
                    : isAgrasFlightParametersListe
                      ? "Prénom — rempli via le contact CRM (ex: Bonjour Marie,)"
                    : isAvisGoogleXlkeyListe
                      ? "Prénom — rempli via le contact CRM (ex: Bonjour Marie,)"
                    : "Ex: John — sera utilisé dans le courriel (ex: Hi John,). Laissez vide pour ne pas personnaliser."
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            />
            {isDronePhase2Liste && (
              <p className="mt-1 text-xs text-muted">
                Peut être rempli automatiquement en choisissant un contact ci-dessus. Sert aussi au sujet :{" "}
                <strong>XLKEY - Phase 2 — … - « nom »</strong>.
              </p>
            )}
            {isPesticideDroneListe && (
              <p className="mt-1 text-xs text-muted">
                Peut être rempli automatiquement en choisissant une ferme puis un contact ci-dessus (salutation « Salut … »).
              </p>
            )}
            {isXlhubDrainageListe && (
              <p className="mt-1 text-xs text-muted">
                Peut être rempli automatiquement en choisissant une ferme puis un contact ci-dessus (prénom pour « Bonjour … »).
              </p>
            )}
            {isDroneImmatriculationTcListe && (
              <p className="mt-1 text-xs text-muted">
                Peut être rempli automatiquement en choisissant une ferme puis un contact ci-dessus (prénom pour « Salut … »).
              </p>
            )}
            {isAgrasFlightParametersListe && (
              <p className="mt-1 text-xs text-muted">
                Peut être rempli automatiquement en choisissant une ferme puis un contact ci-dessus (prénom pour « Bonjour … »).
              </p>
            )}
          </div>

          {isAmbListe && (
            <div>
              <label className="block text-sm font-medium text-text mb-1">Nom de l&apos;organisation (entreprise)</label>
              <input
                type="text"
                value={confirmationName}
                onChange={(e) => setConfirmationName(e.target.value)}
                placeholder='Ex: Fraisière Faucher — utilisé dans le sujet : AMBASSADEUR XLKey · Entente de collaboration « … » et dans le courriel de confirmation.'
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Sert au sujet automatique et au texte du bouton de confirmation. Optionnel : sans nom, le sujet par défaut
                est <strong>AMBASSADEUR XLKey · Entente de collaboration</strong> (sans guillemets).
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text mb-1">Sujet</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                isPhosListe
                  ? "Optionnel si salutation = Prénom Nom — sinon compléter pour le sujet"
                  : isAmbListe
                    ? "Optionnel — laisser vide pour le sujet automatique (voir nom d'organisation)"
                    : isDronePhase2Liste
                      ? "Laisser vide pour le sujet automatique (voir nom pour la salutation)"
                      : isPesticideDroneListe
                        ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                  : isInfoReferenceAssuranceDroneListe
                    ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                      : isXlhubDrainageListe
                        ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                      : isAccesJohndeereListe
                        ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                        : isDroneImmatriculationTcListe
                          ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                          : isAgrasFlightParametersListe
                            ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                          : isAvisGoogleXlkeyListe
                            ? "Laisser vide pour le sujet par défaut du template (voir ci-dessous)"
                    : "Sujet du courriel"
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            />
            {isPhosListe && (
              <p className="mt-1 text-xs text-muted">
                PHOS : sujet envoyé = <strong>Relevé 11 modifié / Amended 2025 Relevé 11 - …</strong> (nom depuis la
                salutation ou ce champ).
              </p>
            )}
            {isAmbListe && (
              <p className="mt-1 text-xs text-muted">
                Entente de collaboration : si le sujet est vide, envoi avec{" "}
                <strong>AMBASSADEUR XLKey · Entente de collaboration &quot;Nom de l&apos;organisation&quot;</strong> (nom
                depuis le champ ci-dessus).
              </p>
            )}
            {isDronePhase2Liste && (
              <p className="mt-1 text-xs text-muted">
                Sujet automatique si ce champ est vide :{" "}
                <strong>
                  XLKEY - Phase 2 — Formation pratique &amp; révision en vol - &quot;
                  {greetingName.trim() || "Participant"}&quot;
                </strong>
                . Vous pouvez aussi saisir un sujet personnalisé ici pour remplacer ce modèle.
              </p>
            )}
            {isPesticideDroneListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{PESTICIDE_DRONE_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser tel quel, ou
                saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isAnnoncePulverisationDroneCaListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{ANNONCE_PULVERISATION_DRONE_CA_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser
                tel quel, ou saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isInfoReferenceAssuranceDroneListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{INFO_REFERENCE_ASSURANCE_DRONE_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser
                tel quel, ou saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isXlhubDrainageListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{XLHUB_DRAINAGE_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser tel quel, ou
                saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isAccesJohndeereListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{ACCES_JOHNDEERE_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser tel quel, ou
                saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isDroneImmatriculationTcListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{DRONE_IMMATRICULATION_TC_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser tel
                quel, ou saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isAgrasFlightParametersListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{agrasDefaultSubject}</strong>. Laissez ce champ vide pour l’utiliser tel
                quel, ou saisissez un autre sujet pour le remplacer.
              </p>
            )}
            {isAvisGoogleXlkeyListe && (
              <p className="mt-1 text-xs text-muted">
                Le sujet est défini par défaut par le template :{" "}
                <strong>{AVIS_GOOGLE_XLKEY_DEFAULT_SUBJECT}</strong>. Laissez ce champ vide pour l’utiliser tel
                quel, ou saisissez un autre sujet pour le remplacer.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">
              {selectedCode === DEFAULT_LIST_TEMPLATE
                ? "Corps du message (optionnel — ajouté après le bouton de confirmation)"
                : "Corps du message (optionnel)"}
            </label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder={
                selectedCode === DEFAULT_LIST_TEMPLATE
                  ? "Texte optionnel ajouté après le bouton de confirmation. Laissez vide pour n'ajouter rien."
                  : "Texte du message. Laissez vide pour le contenu par défaut du template."
              }
              rows={4}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text resize-y"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Pièces jointes (optionnel)</label>
            <div className="flex flex-col gap-2">
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  setAttachedFiles((prev) => [...prev, ...files]);
                  e.target.value = "";
                }}
                className="text-sm text-text file:mr-2 file:py-1 file:px-3 file:rounded file:border file:border-border file:bg-surface file:text-sm"
              />
              {attachedFiles.length > 0 && (
                <ul className="list-disc list-inside text-sm text-muted space-y-1">
                  {attachedFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Paperclip size={14} />
                      <span>{f.name}</span>
                      <button
                        type="button"
                        onClick={() => setAttachedFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="text-danger hover:underline"
                        aria-label="Retirer"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">
              {isAnnoncePulverisationDroneCaListe && annonceBulkRecipientsFile
                ? "Liste des destinataires (ignorée — fichier CSV/Excel sélectionné)"
                : "Liste des destinataires"}
            </label>
            <textarea
              value={emailListText}
              onChange={(e) => setEmailListText(e.target.value)}
              placeholder={
                isAnnoncePulverisationDroneCaListe
                  ? "Optionnel si vous utilisez un fichier CSV/Excel ci-dessus. Sinon : un courriel par ligne."
                  : `Un courriel par ligne, ou ${EMAIL_ADDRESSES_SEPARATOR_HINT.toLowerCase()}`
              }
              rows={6}
              disabled={Boolean(isAnnoncePulverisationDroneCaListe && annonceBulkRecipientsFile)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text font-mono resize-y disabled:opacity-60"
            />
            <p className="mt-1 text-xs text-muted">
              {isAnnoncePulverisationDroneCaListe && annonceBulkRecipientsFile
                ? "L'envoi utilisera le fichier importé (courriel + prénom par ligne)."
                : `${recipients.length} adresse(s) valide(s) détectée(s).`}
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              label={sending ? "Envoi en cours..." : "Envoyer à tous"}
              intent="primary"
              size="md"
              onClick={handleSend}
              state={canSend ? "default" : "disabled"}
            />
            {isAmbListe && (
              <Button
                label={exportingAmbassadeurPdf ? "Export PDF..." : "PDF"}
                intent="default"
                size="md"
                onClick={handleExportAmbassadeurPdf}
                state={canExportAmbassadeurPdf ? "default" : "disabled"}
              />
            )}
          </div>
        </div>
        )}

        {activeTab === "fichier" && (
        <div className="rounded-lg border border-border bg-surface p-4 space-y-4">
          <p className="text-sm text-muted">
            Choisissez un template, l&apos;expéditeur, le sujet, puis importez un fichier destinataires (CSV ou Excel avec colonnes <strong>dossier</strong>, <strong>courriel</strong>) et un ZIP. Vous pouvez zipper le dossier parent : les noms qui doivent correspondre à la colonne « dossier » sont ceux des dossiers qui contiennent directement les fichiers (ex. ZIP avec « test 2 » → à l&apos;intérieur, FPC_Isabelle-Roy, FPC_Julie-Bergeron, etc.). Chaque destinataire reçoit un courriel avec <strong>uniquement les fichiers de son dossier</strong>.
          </p>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Modèle</label>
            <select
              value={templateWithFiles}
              onChange={(e) => setTemplateWithFiles(e.target.value)}
              disabled={loadingTemplates}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            >
              {loadingTemplates ? (
                <option>Chargement...</option>
              ) : (
                templates.map((t) => (
                  <option key={t.code} value={t.code}>
                    {formatTemplateOptionLabel(t)}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Nom de l'expéditeur</label>
            <select
              value={fromNameWithFiles}
              onChange={(e) => setFromNameWithFiles(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            >
              <option value="XLFlow">XLFlow</option>
              <option value="XLKey">XLKey</option>
              <option value="First Phosphate Corp.">First Phosphate Corp.</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Adresse expéditrice</label>
            <select
              value={fromEmailWithFiles}
              onChange={(e) => setFromEmailWithFiles(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            >
              {SENDER_EMAIL_OPTIONS.map((opt) => (
                <option key={opt.value || "default"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">CC (optionnel)</label>
            <input
              type="text"
              value={ccWithFiles}
              onChange={(e) => setCcWithFiles(e.target.value)}
              placeholder={`Ex: a@exemple.com b@exemple.com — ${EMAIL_ADDRESSES_SEPARATOR_HINT.toLowerCase()}`}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            />
            <p className="mt-1 text-xs text-muted">{EMAIL_ADDRESSES_SEPARATOR_HINT}.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">CCI (optionnel)</label>
            <input
              type="text"
              value={cciWithFiles}
              onChange={(e) => setCciWithFiles(e.target.value)}
              placeholder={`Ex: a@exemple.com b@exemple.com — ${EMAIL_ADDRESSES_SEPARATOR_HINT.toLowerCase()}`}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            />
            <p className="mt-1 text-xs text-muted">{EMAIL_ADDRESSES_SEPARATOR_HINT}.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Sujet du courriel</label>
            <input
              type="text"
              value={subjectWithFiles}
              onChange={(e) => setSubjectWithFiles(e.target.value)}
              placeholder={
                templateWithFiles === "phos"
                  ? "Automatique : Relevé 11 modifié / Amended 2025 Relevé 11 - Prénom Nom (depuis le fichier)"
                  : "Sujet du courriel"
              }
              disabled={templateWithFiles === "phos"}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text disabled:opacity-60 disabled:cursor-not-allowed"
            />
            {templateWithFiles === "phos" && (
              <p className="mt-1 text-xs text-muted">
                Pour PHOS, le sujet est généré par destinataire à partir des colonnes prénom et nom du fichier :{" "}
                <strong>Relevé 11 modifié / Amended 2025 Relevé 11 - Prénom Nom</strong>.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Fichier destinataires (CSV ou Excel)</label>
            <p className="text-xs text-muted mb-1">Colonnes requises : <strong>dossier</strong>, <strong>courriel</strong> (ou email). Optionnel : nom, prénom. Plusieurs adresses par cellule acceptées (séparées par virgule, point-virgule ou espace) — un courriel individuel est envoyé à chaque adresse. Excel : .xlsx — CSV : séparateur ; ou ,</p>
            <input
              type="file"
              accept=".csv,.txt,.xlsx"
              onChange={(e) => setRecipientsFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-text file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary/10 file:text-primary"
            />
            {recipientsFile && <span className="text-xs text-muted mt-1 block">{recipientsFile.name}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">ZIP des dossiers</label>
            <p className="text-xs text-muted mb-1">Vous pouvez zipper le dossier parent. Les noms de dossier qui contiennent les fichiers (ex. FPC_Isabelle-Roy) doivent correspondre exactement à la colonne « dossier » du fichier destinataires.</p>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-text file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary/10 file:text-primary"
            />
            {zipFile && <span className="text-xs text-muted mt-1 block">{zipFile.name}</span>}
          </div>
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            Chaque destinataire ne reçoit que les fichiers de son propre dossier. Les noms des dossiers qui contiennent les fichiers (dans le ZIP) doivent correspondre exactement à la colonne « dossier » du CSV/Excel.
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button
              label={sendingWithFiles ? "Envoi en cours..." : "Envoyer avec pièces jointes"}
              intent="primary"
              size="md"
              onClick={handleSendWithFiles}
              state={canSendWithFiles ? "default" : "disabled"}
            />
          </div>
        </div>
        )}

        {activeTab === "registre" && (
          <div className="rounded-lg border border-border bg-surface p-4">
            <h2 className="text-sm font-semibold text-text flex items-center gap-2 mb-4">
              <BookOpen size={16} />
              Registre des envois
            </h2>
            <p className="text-sm text-muted mb-4">
              Liste des courriels envoyés (ordre anti-chronologique). Statut : Envoyé = remis au serveur ; Reçu / Ouvert / Répondu = suivi si activé. <strong>Ouvertures</strong> = nombre de fois que le courriel a été ouvert (pixel) ; <strong>Temps lecture (est.)</strong> = durée entre première et dernière ouverture (affichée si ouvert au moins 2 fois). Le registre affiche uniquement le <strong>destinataire principal (To)</strong> ; les adresses en CC/CCI ne sont pas listées par envoi.
            </p>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <label className="text-sm text-text">Filtrer par courriel (destinataire principal) :</label>
              <input
                type="email"
                value={registerFilterInput}
                onChange={(e) => setRegisterFilterInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setRegisterFilterApplied(registerFilterInput.trim())}
                placeholder="ex: valerie@firstphosphate.com"
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text w-64"
              />
              <Button
                label="Filtrer"
                intent="secondary"
                size="sm"
                onClick={() => setRegisterFilterApplied(registerFilterInput.trim())}
              />
              {registerFilterApplied && (
                <Button
                  label="Effacer le filtre"
                  intent="ghost"
                  size="sm"
                  onClick={() => { setRegisterFilterApplied(""); setRegisterFilterInput(""); }}
                />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <label className="text-sm text-text">Filtrer par date (optionnel) :</label>
              <input
                type="date"
                value={registerDateFrom}
                onChange={(e) => setRegisterDateFrom(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text"
              />
              <span className="text-sm text-muted">au</span>
              <input
                type="date"
                value={registerDateTo}
                onChange={(e) => setRegisterDateTo(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Button
                label={registerExporting ? "Export en cours..." : "Exporter en CSV"}
                intent="secondary"
                size="sm"
                onClick={handleExportRegisterCsv}
                state={registerExporting ? "disabled" : "default"}
              />
              <Button
                label="Exporter en Excel"
                intent="secondary"
                size="sm"
                onClick={handleExportRegisterExcel}
                state={registerExporting ? "disabled" : "default"}
              />
              <span className="text-xs text-muted">Exporte avec les filtres appliqués (courriel, date). Toutes les colonnes.</span>
            </div>
            {registerLoading ? (
              <p className="text-sm text-muted">Chargement...</p>
            ) : registerItems.length === 0 ? (
              <p className="text-sm text-muted">Aucun envoi enregistré.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2 font-medium text-text">Date</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Destinataire</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Sujet</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Modèle</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Expéditeur</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Statut</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Ouvertures</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Temps lecture (est.)</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Erreur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registerItems.map((row) => (
                      <tr key={row.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 px-2 text-text whitespace-nowrap">
                          {row.sent_at ? new Date(row.sent_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }) : "—"}
                        </td>
                        <td className="py-2 px-2 text-text">{row.to_email}</td>
                        <td className="py-2 px-2 text-text min-w-[180px] max-w-[400px] truncate" title={row.subject}>{row.subject}</td>
                        <td className="py-2 px-2 text-muted">{row.template_code ?? "—"}</td>
                        <td className="py-2 px-2 text-muted">{row.from_email ?? row.from_name ?? "—"}</td>
                        <td className="py-2 px-2">
                          <span className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                            row.status === "sent" && "bg-success/15 text-success",
                            row.status === "delivered" && "bg-info/15 text-info",
                            row.status === "opened" && "bg-info/20 text-info",
                            row.status === "replied" && "bg-primary/15 text-primary",
                            row.status === "failed" && "bg-danger/15 text-danger"
                          )}>
                            {STATUS_LABELS[row.status] ?? row.status}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-text tabular-nums">{row.open_count ?? 0}</td>
                        <td className="py-2 px-2 text-muted whitespace-nowrap">
                          {row.read_duration_seconds != null ? formatReadDuration(row.read_duration_seconds) : "—"}
                        </td>
                        <td className="py-2 px-2 text-danger text-xs min-w-[120px] max-w-[280px] truncate" title={row.error_message ?? undefined}>
                          {row.error_message ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {registerTotal > registerItems.length && (
                  <p className="text-xs text-muted mt-2">Affichage des {registerItems.length} premiers sur {registerTotal}.</p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "sondage" && (
          <div className="rounded-lg border border-border bg-surface p-4">
            <h2 className="text-sm font-semibold text-text flex items-center gap-2 mb-4">
              <ClipboardList size={16} />
              Sondage : présentation XLKey
            </h2>
            <p className="text-sm text-muted mb-4">
              Réponses au sondage envoyées via le template « Suivi : présentation XLKey ». Chaque ligne est une réponse
              (lien personnel ou, si « partageable », une des réponses issues du même lien retransmis). L’utilisateur XLFlow
              est affiché si l’adresse du relais correspond à un compte.
            </p>
            {surveyLoading ? (
              <p className="text-sm text-muted">Chargement…</p>
            ) : surveyLoadError ? (
              <div className="rounded-md border border-danger/30 bg-danger/5 p-3 text-sm space-y-2">
                <p className="text-danger">{surveyLoadError}</p>
                <Button label="Réessayer" size="sm" onClick={() => loadSurveyResponses()} />
              </div>
            ) : surveyItems.length === 0 ? (
              <p className="text-sm text-muted">Aucune réponse enregistrée pour l’instant.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse min-w-[960px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2 font-medium text-text whitespace-nowrap">Réponse</th>
                      <th className="text-left py-2 px-2 font-medium text-text whitespace-nowrap">Partageable</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Courriel</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Utilisateur XLFlow</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q1</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q2</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q3</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q3 autre</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q5</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q6</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q7</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q7 autre</th>
                      <th className="text-left py-2 px-2 font-medium text-text">Q8</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveyItems.map((row) => {
                      const a = (row.answers ?? {}) as Record<string, unknown>;
                      return (
                        <tr key={row.id} className="border-b border-border/50 hover:bg-muted/30 align-top">
                          <td className="py-2 px-2 text-text whitespace-nowrap">
                            {row.submitted_at
                              ? new Date(row.submitted_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })
                              : "—"}
                          </td>
                          <td className="py-2 px-2 text-muted whitespace-nowrap">{row.broadcast ? "Oui" : "—"}</td>
                          <td className="py-2 px-2 text-text max-w-[200px] break-all">{row.to_email}</td>
                          <td className="py-2 px-2 text-muted text-xs">
                            {row.matched_user
                              ? row.matched_user.display_name || row.matched_user.email
                              : "—"}
                          </td>
                          <td className="py-2 px-2 text-xs max-w-[140px]">{formatSurveyAnswerCell(a["q1"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[140px]">{formatSurveyAnswerCell(a["q2"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[180px]">{formatSurveyAnswerCell(a["q3"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[120px]">{formatSurveyAnswerCell(a["q3_other"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[140px]">{formatSurveyAnswerCell(a["q5"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[140px]">{formatSurveyAnswerCell(a["q6"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[180px]">{formatSurveyAnswerCell(a["q7"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[120px]">{formatSurveyAnswerCell(a["q7_other"])}</td>
                          <td className="py-2 px-2 text-xs max-w-[220px] whitespace-pre-wrap">{formatSurveyAnswerCell(a["q8"])}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {surveyTotal > surveyItems.length && (
                  <p className="text-xs text-muted mt-2">
                    Affichage des {surveyItems.length} premiers sur {surveyTotal}.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {(result || resultWithFiles) && (
          <div className={cn(
            "rounded-lg border p-4",
            (activeTab === "fichier"
              ? ((resultWithFiles?.errors?.length ?? 0) === 0 && (resultWithFiles?.skipped_no_email?.length ?? 0) === 0)
              : (result?.errors?.length === 0))
              ? "border-success/30 bg-success/5" : "border-border bg-surface"
          )}>
            <h2 className="text-sm font-semibold text-text flex items-center gap-2">
              <FileText size={16} />
              Résultat
            </h2>
            <p className="mt-2 text-sm text-text">
              {(activeTab === "fichier" ? resultWithFiles : result)?.sent} courriel(s) envoyé(s) sur {(activeTab === "fichier" ? resultWithFiles : result)?.total}.
              {((activeTab === "fichier" ? resultWithFiles : result)?.skipped_duplicates ?? 0) > 0 && (
                <span className="text-muted">
                  {" "}
                  ({((activeTab === "fichier" ? resultWithFiles : result)?.skipped_duplicates ?? 0)} doublon(s) ignoré(s).)
                </span>
              )}
            </p>
            {activeTab === "liste" && ((result?.errors?.length ?? 0) > 0) && (
              <ul className="mt-2 text-sm text-danger list-disc list-inside">
                {result!.errors.map((e) => (
                  <li key={e.email}>{e.email}: {e.error}</li>
                ))}
              </ul>
            )}
            {activeTab === "fichier" && resultWithFiles && (() => {
              const errors = resultWithFiles.errors ?? [];
              const skipped = resultWithFiles.skipped_no_email ?? [];
              const hasNonSent = errors.length > 0 || skipped.length > 0;
              if (!hasNonSent) return null;
              type Item = { key: string; label: string; reason: string; reasonKind: "dossier" | "courriel" | "erreur" };
              const items: Item[] = [
                ...errors.map((e) => ({
                  key: `err-${e.email}-${e.dossier ?? ""}`,
                  label: e.dossier ? `${e.email} (${e.dossier})` : e.email,
                  reason: e.error,
                  reasonKind: e.error.includes("Dossier non trouvé") ? "dossier" as const : "erreur" as const,
                })),
                ...skipped.map((s) => ({
                  key: `skip-${s.dossier}`,
                  label: [s.dossier, s.prenom, s.nom].filter(Boolean).join(" — "),
                  reason: s.raison ?? "Courriel manquant ou invalide",
                  reasonKind: "courriel" as const,
                })),
              ];
              const reasonClass = (kind: Item["reasonKind"]) =>
                kind === "dossier" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" :
                kind === "courriel" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300" :
                "bg-danger/15 text-danger";
              return (
                <div className="mt-3">
                  <p className="text-sm font-medium text-text mb-2">Non envoyés ({items.length}) — avec raison :</p>
                  <ul className="space-y-1.5 text-sm">
                    {items.map(({ key, label, reason, reasonKind }) => (
                      <li key={key} className="flex flex-wrap items-center gap-2">
                        <span className="text-text">{label}</span>
                        <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0", reasonClass(reasonKind))}>
                          {reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            {activeTab === "fichier" && resultWithFiles && (
              <div className="mt-3 pt-3 border-t border-border">
                <Button
                  label="Exporter le récapitulatif en CSV"
                  intent="secondary"
                  size="md"
                  onClick={() => exportSendResultToCsv(resultWithFiles)}
                />
                <p className="text-xs text-muted mt-1">
                  Contient : envoyés, échecs (avec raison), non envoyés (courriel manquant ou dossier absent du ZIP).
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
